/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { useCallback, useEffect, useMemo } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router"
import { Empty, Table, TableProps } from "@duxton-web/ui"
import dayjs from "dayjs"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import useSingleStoreUser from "hooks/useSingleStoreUser"
import {
  EventCategory,
  EventAction,
  EventCategoryEng,
  EventActionEng,
} from "constants/tracking"
import { FEEDBACK } from "constants/routes"
import { TroyWebTracker } from "utils/tracking"
import { datadogRum } from "@datadog/browser-rum"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { useFetchIncidentListQuery, FetchIncidentListRequest } from "queries"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { RenderResolutionType, RenderShortOrderNumber } from "./UIHelper"
import { IncidentListFilter } from "./IncidentListFilter"
import { defaultPageSizeIncidents } from "../consts"
import useFeedbackStorelist from "../FeedbackFilters/hooks/useFeedbackStoreList"
import IncidentsDrawer from "./IncidentsDrawer"
import {
  formatDateForDisplay,
  convertBrowserTimeToUTC,
  getDefaultFromTimeBrowser,
  getDefaultEndTimeBrowser,
} from "./utils"

const columns = [
  {
    title: "Date",
    dataIndex: "incidentCreatedAt",
    key: "incidentCreatedAt",
    render: (text: string) => {
      // text is UTC from BE, convert to browser time for display
      return dayjs
        .utc(text)
        .local()
        .format(formatDateForDisplay)
    },
  },
  {
    title: "Short Order Number",
    dataIndex: "shortOrderNumber",
    key: "shortOrderNumber",
    render: (text: string, record: any) => (
      <RenderShortOrderNumber
        shortOrderNumber={text}
        hasRead={record.hasRead}
      />
    ),
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
  },
  {
    title: "Resolution",
    dataIndex: "appeaseStatus",
    key: "appeaseStatus",
    render: (value: any, record: any) => (
      <RenderResolutionType
        appeaseStatus={value}
        chargebackStatus={record.chargebackStatus}
        appealStatus={record.appealStatus}
      />
    ),
  },
]

export const IncidentList = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { isSingleStoreUser, storeID } = useSingleStoreUser()
  const queryParams = new URLSearchParams(location.search)

  const merchantID =
    queryParams.get("merchantID") ||
    (isSingleStoreUser ? (storeID as string) : "")

  // Create string representations of arrays for dependency comparison
  const incidentTypeStr = queryParams.get("incidentType") || ""
  const resolutionTypeStr = queryParams.get("resolutionType") || ""

  const incidentType = useMemo(
    () => (incidentTypeStr ? incidentTypeStr.split(",") : []),
    [incidentTypeStr],
  )

  const resolutionType = useMemo(
    () => (resolutionTypeStr ? resolutionTypeStr.split(",") : []),
    [resolutionTypeStr],
  )

  // Convert browser time from filter to UTC for API calls
  const fromTime = queryParams.get("fromTime")
    ? convertBrowserTimeToUTC(queryParams.get("fromTime") as string, false) // start of day
    : convertBrowserTimeToUTC(getDefaultFromTimeBrowser(), false)
  const endTime = queryParams.get("endTime")
    ? convertBrowserTimeToUTC(queryParams.get("endTime") as string, true) // end of day
    : convertBrowserTimeToUTC(getDefaultEndTimeBrowser(), true)

  const page = queryParams.get("page") || 1

  const textMap = useTranslationsMapTyped({
    emptyFilteredByIncidentType: "incidents.emptyData.incidentType",
    emptyFilteredByResolutionType: "incidents.emptyData.resolutionType",
  })

  const {
    flags: { fetchIncidentList: isFetchIncidentListRtkEnabled },
    isLoading: isFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const incidentParams: FetchIncidentListRequest = {
    merchantID,
    fromTime,
    endTime,
    incidentType,
    resolutionType,
    page: Number(page),
  }

  // RTK Query path — skipped when flag is off
  const {
    data: rtkData,
    isFetching: rtkIsFetching,
    refetch,
  } = useFetchIncidentListQuery(incidentParams, {
    skip: !merchantID || !isFetchIncidentListRtkEnabled,
  })

  // Legacy Redux path — always selected but only active when flag is off
  const {
    incidents: reduxIncidents,
    isLoading: reduxIsLoading,
    total: reduxTotal,
  } = useSelector((state: AppState) => state.incidents, shallowEqual)

  // Legacy fetch — only dispatched when flag is off
  useEffect(() => {
    if (merchantID && !isFlagLoading && !isFetchIncidentListRtkEnabled) {
      dispatch(actionCreator.incidents.fetchIncidentList(incidentParams))
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.fetchIncidentList,
        labels: {
          MERCHANT_ID: merchantID,
          IS_RTK_ENABLED: String(isFetchIncidentListRtkEnabled),
          FROM_RTK_QUERY: "false",
          FROM_OLD_REDUX: "true",
          ACTION_NAME: "fetchIncidentList",
        },
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    merchantID,
    fromTime,
    endTime,
    incidentTypeStr,
    resolutionTypeStr,
    dispatch,
    page,
    isFetchIncidentListRtkEnabled,
    isFlagLoading,
  ]) // incidentType and resolutionType are represented by incidentTypeStr and resolutionTypeStr, exclude incidentParams from dependencies replaced with it's primitive values

  // RTK Query path tracking — fires when RTK flag is on and merchantID is available
  useEffect(() => {
    if (merchantID && !isFlagLoading && isFetchIncidentListRtkEnabled) {
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.fetchIncidentList,
        labels: {
          MERCHANT_ID: merchantID,
          IS_RTK_ENABLED: String(isFetchIncidentListRtkEnabled),
          FROM_RTK_QUERY: "true",
          FROM_OLD_REDUX: "false",
          ACTION_NAME: "fetchIncidentList",
        },
      })
    }
  }, [merchantID, isFlagLoading, isFetchIncidentListRtkEnabled])

  // Derive active values based on flag
  const incidents = isFetchIncidentListRtkEnabled
    ? rtkData?.incidents ?? []
    : reduxIncidents
  const isLoading = isFetchIncidentListRtkEnabled
    ? rtkIsFetching
    : reduxIsLoading
  let total = 0
  if (isFetchIncidentListRtkEnabled) {
    if (rtkData) {
      // When there are no incidents and no more pages, total should be 0
      if (rtkData.incidents.length === 0 && !rtkData.hasMore) {
        total = 0
      } else {
        total = rtkData.hasMore
          ? Number(page) * defaultPageSizeIncidents + 1
          : Number(page) * defaultPageSizeIncidents
      }
    }
  } else {
    total = reduxTotal
  }

  const {
    handleFilter,
    filteredList,
    isFetchingOutletsSearch,
  } = useFeedbackStorelist(EventCategory.incidentsList)

  const handleQueryChange = useCallback(
    (params: Partial<Record<string, string | string[]>>) => {
      const newParams = new URLSearchParams(location.search)

      // Update each parameter
      Object.entries(params).forEach(([key, value]) => {
        if (
          value === undefined ||
          value === null ||
          (Array.isArray(value) && value.length === 0)
        ) {
          newParams.delete(key)
        } else if (Array.isArray(value)) {
          // For array values, we want to preserve commas
          newParams.set(key, value.join(","))
        } else {
          newParams.set(key, value)
        }
      })

      history.replace({
        search: newParams.toString() ? `?${newParams.toString()}` : "",
      })
    },
    [history, location.search],
  )

  // Fetch store list if not available
  useEffect(() => {
    if (!filteredList?.length) {
      handleFilter({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  // Set initial merchantID from filteredList if empty
  useEffect(() => {
    if (!merchantID && filteredList?.length) {
      const initialMerchantID = filteredList[0]?.merchantID

      if (initialMerchantID) {
        handleQueryChange({ merchantID: initialMerchantID })
        TroyWebTracker.sendEvent({
          category: EventCategory.incidentListFromDefault,
          action: EventAction.loadPage,
          labels: {
            MERCHANT_ID: initialMerchantID,
          },
        })
      }
    }
  }, [filteredList, merchantID, handleQueryChange])

  const paginateTable = (pagination: any) => {
    const { current } = pagination

    handleQueryChange({ page: current })
  }

  const onRow: TableProps["onRow"] = useCallback(
    record => ({
      onClick: () => {
        if (!record?.incidentID) {
          datadogRum.addError("Incident ID is missing from the record", {
            tags: {
              context: "IncidentList",
              merchantID,
            },
          })

          return
        }

        // Refetch/re-dispatch incident list to update hasRead status
        // Delay to allow backend time to update hasRead before re-fetching
        setTimeout(() => {
          if (isFetchIncidentListRtkEnabled) {
            refetch()
          } else {
            dispatch(actionCreator.incidents.fetchIncidentList(incidentParams))
          }
        }, 300)

        const currentParams = new URLSearchParams(location.search)
        currentParams.set("merchantID", merchantID)
        history.push(
          `${FEEDBACK}/incidents/${
            record.incidentID
          }?${currentParams.toString()}`,
        )
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      history,
      merchantID,
      location.search,
      isFetchIncidentListRtkEnabled,
      refetch,
      dispatch,
      fromTime,
      endTime,
      incidentType,
      resolutionType,
      page,
    ],
  )

  const renderDescription = () => {
    if (resolutionType.length > 0) {
      return textMap.emptyFilteredByResolutionType
    }

    return textMap.emptyFilteredByIncidentType
  }

  return (
    <>
      <IncidentsDrawer />
      <IncidentListFilter
        storeList={filteredList}
        merchantID={merchantID}
        setMerchantID={id => handleQueryChange({ merchantID: id })}
        setDateRange={(from, end) =>
          handleQueryChange({ fromTime: from, endTime: end })
        }
        fromTime={fromTime}
        endTime={endTime}
        selectedIncidentType={incidentType}
        setSelectedIncidentType={types =>
          handleQueryChange({ incidentType: types })
        }
        selectedResolutionType={resolutionType}
        setSelectedResolutionType={types =>
          handleQueryChange({ resolutionType: types })
        }
        isSingleStoreUser={isSingleStoreUser}
        handleFilter={handleFilter}
        isFetchingOutletsSearch={isFetchingOutletsSearch}
      />
      <Table
        columns={columns}
        dataSource={incidents}
        loading={isLoading}
        pagination={{
          pageSize: defaultPageSizeIncidents,
          hideOnSinglePage: true,
          total,
          defaultCurrent: 1,
          showSizeChanger: false,
          current: Number(page),
        }}
        onChange={paginateTable}
        onRow={onRow}
        locale={{
          emptyText: (
            <Empty
              description={renderDescription()}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />
    </>
  )
}
